import React, { useState } from 'react';
import { Save, Globe, Shield, CreditCard, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function Toggle({ defaultOn = true }) {
  const [on, setOn] = useState(defaultOn);
  return (
    <button onClick={() => setOn(!on)} className={`w-10 h-5 rounded-full transition-colors flex items-center px-0.5 ${on ? 'bg-primary' : 'bg-muted'}`}>
      <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );
}

const SECTIONS = [
  {
    title: 'General', icon: Globe,
    fields: [
      { label: 'Platform Name', type: 'text', value: 'XceedEdge' },
      { label: 'Support Email', type: 'email', value: 'support@xceedge.in' },
      { label: 'Platform URL', type: 'text', value: 'https://xceedge.in' },
    ],
  },
  {
    title: 'Email & Notifications', icon: Mail,
    fields: [
      { label: 'SMTP Host', type: 'text', value: 'smtp.xceedge.in' },
      { label: 'From Email', type: 'email', value: 'noreply@xceedge.in' },
    ],
  },
  {
    title: 'Payments', icon: CreditCard,
    fields: [
      { label: 'Razorpay Key ID', type: 'text', value: 'rzp_live_xxxxx' },
      { label: 'Platform Commission (%)', type: 'number', value: '20' },
    ],
  },
];

const TOGGLES = [
  { label: 'User Registration', desc: 'Allow new users to register', on: true },
  { label: 'Course Enrollment', desc: 'Allow students to enroll in courses', on: true },
  { label: 'Instructor Applications', desc: 'Accept new instructor applications', on: true },
  { label: 'Maintenance Mode', desc: 'Show maintenance page to users', on: false },
  { label: 'Email Notifications', desc: 'Send transactional emails', on: true },
  { label: 'Two-Factor Auth', desc: 'Require 2FA for admin logins', on: false },
  { label: 'Course Reviews', desc: 'Allow students to leave reviews', on: true },
  { label: 'Certificates', desc: 'Issue certificates on completion', on: true },
];

export default function AdminSystemSettings() {
  const [saved, setSaved] = useState(false);
  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold">System Settings</h2>
        <Button onClick={save} className={`gap-2 ${saved ? 'bg-green-600 hover:bg-green-700' : ''}`}>
          <Save className="w-4 h-4" /> {saved ? 'Saved!' : 'Save All'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {SECTIONS.map(sec => {
          const Icon = sec.icon;
          return (
            <div key={sec.title} className="bg-card rounded-xl border p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-primary" />
                <h3 className="font-semibold">{sec.title}</h3>
              </div>
              {sec.fields.map(f => (
                <div key={f.label}>
                  <Label className="text-xs">{f.label}</Label>
                  <Input type={f.type} className="mt-1 h-8 text-sm" defaultValue={f.value} />
                </div>
              ))}
            </div>
          );
        })}

        <div className="bg-card rounded-xl border p-5 space-y-4 lg:col-span-2">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">Platform Toggles</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {TOGGLES.map(t => (
              <div key={t.label} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <div>
                  <p className="text-sm font-medium">{t.label}</p>
                  <p className="text-xs text-muted-foreground">{t.desc}</p>
                </div>
                <Toggle defaultOn={t.on} />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-xl border p-5 space-y-3 lg:col-span-2">
          <h3 className="font-semibold">Announcement Banner</h3>
          <Textarea className="h-20 text-sm" placeholder="Type a platform-wide announcement (leave blank to hide)..." />
          <div className="flex gap-3">
            <Button size="sm" className="gap-1.5"><Save className="w-3.5 h-3.5" /> Publish</Button>
            <Button size="sm" variant="outline">Clear</Button>
          </div>
        </div>
      </div>
    </div>
  );
}